import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      componentStack: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log detailed error information
    console.group('🔴 ErrorBoundary caught an error');
    console.error('Error:', error);
    console.error('Error message:', error?.message);
    console.error('Error stack:', error?.stack);
    console.error('Component stack:', errorInfo?.componentStack);
    console.groupEnd();
    
    this.setState({
      errorInfo,
      componentStack: errorInfo?.componentStack
    });
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      componentStack: null 
    });
  };

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV === 'development';
      
      return this.props.fallback || (
        <div style={{ 
          padding: '2rem', 
          maxWidth: '800px',
          margin: '2rem auto',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontSize: '2rem', marginRight: '0.5rem' }}>⚠️</span>
            <h2 style={{ margin: 0, color: '#dc2626' }}>Something went wrong</h2>
          </div>
          
          <div style={{ 
            backgroundColor: '#fff', 
            padding: '1rem', 
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #e5e7eb'
          }}>
            <p style={{ 
              fontFamily: 'monospace', 
              fontSize: '0.875rem',
              margin: 0,
              color: '#dc2626',
              wordBreak: 'break-word'
            }}>
              {this.state.error?.message || 'Unknown error'}
            </p>
          </div>

          {isDev && this.state.error?.stack && (
            <details style={{ marginBottom: '1rem' }}>
              <summary style={{ 
                cursor: 'pointer', 
                color: '#6b7280',
                fontSize: '0.875rem',
                marginBottom: '0.5rem'
              }}>
                View error stack (dev only)
              </summary>
              <pre style={{ 
                backgroundColor: '#1f2937', 
                color: '#f9fafb',
                padding: '1rem', 
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '0.75rem',
                maxHeight: '200px'
              }}>
                {this.state.error.stack}
              </pre>
            </details>
          )}

          {isDev && this.state.componentStack && (
            <details style={{ marginBottom: '1rem' }}>
              <summary style={{ 
                cursor: 'pointer', 
                color: '#6b7280',
                fontSize: '0.875rem',
                marginBottom: '0.5rem'
              }}>
                View component stack (dev only)
              </summary>
              <pre style={{ 
                backgroundColor: '#1f2937', 
                color: '#f9fafb',
                padding: '1rem', 
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '0.75rem',
                maxHeight: '200px'
              }}>
                {this.state.componentStack}
              </pre>
            </details>
          )}

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              onClick={this.handleRetry}
              style={{ 
                padding: '0.5rem 1rem', 
                cursor: 'pointer',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px'
              }}
            >
              Try Again
            </button>
            <button 
              onClick={() => window.location.reload()} 
              style={{ 
                padding: '0.5rem 1rem', 
                cursor: 'pointer',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px'
              }}
            >
              Reload Page
            </button>
            <button 
              onClick={() => window.history.back()} 
              style={{ 
                padding: '0.5rem 1rem', 
                cursor: 'pointer',
                backgroundColor: '#e5e7eb',
                color: '#374151',
                border: 'none',
                borderRadius: '4px'
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
